import React, { useEffect, useState, useRef } from 'react';
import { isNil } from 'lodash';
import { useLatest } from '@fiverr-private/hooks';
import { LazyComponentProps, PromiseResult } from './types';

const EmptyComponent = () => null;

const emptyFunction = () => undefined;

const LazyComponent = <T extends object>({
  lazyImport,
  shouldImport = true,
  fallback = <EmptyComponent />,
  placeholder = <EmptyComponent />,
  beforeLoad = emptyFunction,
  afterLoad = emptyFunction,
  onError = emptyFunction,
  componentProps,
}: LazyComponentProps<T>) => {
  const [isLoading, setIsLoading] = useState(true);
  const componentRef = useRef<PromiseResult | null>(null);
  const isImported = !isNil(componentRef.current);
  const Component = isImported ? componentRef.current?.default : null;
  const latestImport = useLatest(lazyImport);
  const latestCallbacks = useLatest({
    beforeLoad,
    afterLoad,
    onError,
  });

  useEffect(() => {
    const lazyLoadComponent = async () => {
      try {
        latestCallbacks.current.beforeLoad();
        componentRef.current = await latestImport.current();
        latestCallbacks.current.afterLoad();
      } catch (error) {
        latestCallbacks.current.onError(error);
      }

      setIsLoading(false);
    };

    if (shouldImport && !isImported) {
      lazyLoadComponent();
    }
  }, [shouldImport, isImported, latestCallbacks, latestImport]);

  if (isLoading) {
    return placeholder;
  }

  return Component ? <Component {...componentProps} /> : fallback;
};

LazyComponent.displayName = 'LazyComponent';

export default LazyComponent;
